import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Minus, Plus, ArrowLeft, Check, Truck, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
        
        // Fetch related products
        const relatedRes = await axios.get(`${API}/products?category=${response.data.category}`);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== productId).slice(0, 3));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      toast.success(`${quantity}x ${product.name} added to cart!`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-24 md:py-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="aspect-square skeleton rounded-3xl" />
            <div className="space-y-4">
              <div className="h-8 skeleton w-1/4" />
              <div className="h-12 skeleton w-3/4" />
              <div className="h-24 skeleton" />
              <div className="h-12 skeleton w-1/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen py-24 md:py-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Product not found</h1>
          <Link to="/shop">
            <Button className="btn-primary">Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="product-page">
      <div className="container-custom">
        {/* Back button */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <div className="aspect-square rounded-3xl overflow-hidden border border-border/50 glow-orange">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            <Badge className="absolute top-4 left-4 badge-accent capitalize">
              {product.category}
            </Badge>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <p className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
                Grad P Fawkes™ Official
              </p>
              <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }} data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {product.description}
              </p>
            </div>

            <div className="text-4xl font-black text-secondary" data-testid="product-price">
              ${product.price.toFixed(2)}
            </div>

            {/* Product Details */}
            {product.details && (
              <div className="space-y-3">
                {product.details.sizes && (
                  <div>
                    <p className="text-sm font-semibold mb-2">Available Sizes</p>
                    <div className="flex flex-wrap gap-2">
                      {product.details.sizes.map(size => (
                        <span key={size} className="px-4 py-2 bg-muted rounded-full text-sm">
                          {size}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {product.details.material && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Material:</span> {product.details.material}
                  </p>
                )}
                {product.details.servings && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold">Supply:</span> {product.details.servings}
                  </p>
                )}
              </div>
            )}

            <Separator />

            {/* Quantity & Add to Cart */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3 bg-muted rounded-full p-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="decrease-quantity"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-12 text-center font-bold text-lg" data-testid="quantity-display">
                  {quantity}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="increase-quantity"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <Button
                className="btn-primary flex-1 sm:flex-none h-14 px-8 text-lg"
                onClick={handleAddToCart}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart - ${(product.price * quantity).toFixed(2)}
              </Button>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Truck className="w-5 h-5 text-secondary" />
                <span>Free shipping over $50</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Shield className="w-5 h-5 text-secondary" />
                <span>Secure checkout</span>
              </div>
            </div>

            {/* Stock info */}
            <div className="flex items-center gap-2 text-sm">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-muted-foreground">
                {product.stock > 20 ? 'In Stock' : `Only ${product.stock} left in stock`}
              </span>
            </div>
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-24">
            <h2 className="section-header text-3xl mb-8">You May Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((relatedProduct, index) => (
                <motion.div
                  key={relatedProduct.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/product/${relatedProduct.id}`}>
                    <Card className="product-card group">
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={relatedProduct.image_url}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-bold mb-2 line-clamp-1">{relatedProduct.name}</h3>
                        <p className="text-secondary font-bold">${relatedProduct.price.toFixed(2)}</p>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
